/**
 * Facebook Pixel Tracking Utility
 * 
 * Supports dual-pixel tracking (platform + tenant pixels)
 * Based on Facebook Pixel Standard Events
 */

declare global {
    interface Window {
        fbq?: (...args: any[]) => void;
        _fbq?: (...args: any[]) => void;
    }
}

interface FacebookPixelItem {
    id: string;
    quantity: number;
    item_price?: number;
}

interface UserData {
    em?: string; // email
    ph?: string; // phone
    fn?: string; // first name
    ln?: string; // last name
    ct?: string; // city
    st?: string; // state
    zp?: string; // zip/postcode
    country?: string;
}

class FacebookPixel {
    private platformPixelId: string = '';
    private tenantPixelId: string = '';
    private initialized = false;
    private userData: UserData = {};

    /**
     * Initialize Facebook Pixel(s)
     * Can be called multiple times for dual-pixel tracking
     */
    init(pixelId: string, type: 'platform' | 'tenant' = 'platform') {
        if (!pixelId) {
            console.warn('[Facebook Pixel] No Pixel ID provided');
            return;
        }

        // Store pixel IDs
        if (type === 'platform') {
            this.platformPixelId = pixelId;
        } else {
            this.tenantPixelId = pixelId;
        }

        // Only load the script once
        if (!this.initialized) {
            this.loadPixelScript();
            this.initialized = true;
        }

        // Initialize this specific pixel
        if (window.fbq) {
            window.fbq('init', pixelId, this.userData);
        }

    }

    /**
     * Load Facebook Pixel script
     */
    private loadPixelScript() {
        // Create fbq function
        const fbq: any = function () {
            fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments);
        };

        if (!window.fbq) {
            window.fbq = fbq;
        }

        fbq.push = fbq;
        fbq.loaded = true;
        fbq.version = '2.0';
        fbq.queue = [];

        // Load script
        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://connect.facebook.net/en_US/fbq.js';
        document.head.appendChild(script);

        // Add noscript pixel
        const noscript = document.createElement('noscript');
        const img = document.createElement('img');
        img.height = 1;
        img.width = 1;
        img.style.display = 'none';
        img.src = 'https://www.facebook.com/tr?id=&ev=PageView&noscript=1';
        noscript.appendChild(img);
        document.body.appendChild(noscript);

    }

    /**
     * Track page view
     */
    pageView() {
        if (!this.initialized || !window.fbq) return;

        window.fbq('track', 'PageView');

    }

    /**
     * Track product view
     */
    viewContent(params: {
        content_ids: string[];
        content_name: string;
        content_type?: string;
        value?: number;
        currency?: string;
    }) {
        if (!this.initialized || !window.fbq) return;

        const eventData = {
            content_ids: params.content_ids,
            content_name: params.content_name,
            content_type: params.content_type || 'product',
            value: params.value,
            currency: params.currency || 'MYR',
        };

        window.fbq('track', 'ViewContent', eventData);

    }

    /**
     * Track add to cart
     */
    addToCart(params: {
        content_ids: string[];
        content_name: string;
        content_type?: string;
        value: number;
        currency?: string;
        quantity?: number;
    }) {
        if (!this.initialized || !window.fbq) return;

        const eventData = {
            content_ids: params.content_ids,
            content_name: params.content_name,
            content_type: params.content_type || 'product',
            value: params.value,
            currency: params.currency || 'MYR',
            num_items: params.quantity || 1,
        };

        window.fbq('track', 'AddToCart', eventData);

    }

    /**
     * Track checkout initiation
     */
    initiateCheckout(params: {
        content_ids: string[];
        contents?: FacebookPixelItem[];
        value: number;
        currency?: string;
        num_items?: number;
    }) {
        if (!this.initialized || !window.fbq) return;

        const eventData = {
            content_ids: params.content_ids,
            contents: params.contents,
            value: params.value,
            currency: params.currency || 'MYR',
            num_items: params.num_items || params.content_ids.length,
        };

        window.fbq('track', 'InitiateCheckout', eventData);

    }

    /**
     * Track purchase
     * Returns event data including event_id for server-side deduplication
     */
    purchase(params: {
        content_ids: string[];
        contents?: FacebookPixelItem[];
        value: number;
        currency?: string;
        num_items?: number;
        order_id?: string;
        eventId?: string; // Optional: Override event ID if already generated
    }): { eventId: string; fbp: string | null; fbc: string | null } {
        if (!this.initialized || !window.fbq) {
            return { eventId: '', fbp: null, fbc: null };
        }

        // Generate unique event ID for deduplication OR use provided one
        const eventId = params.eventId || this.generateEventId();

        const eventData = {
            content_ids: params.content_ids,
            contents: params.contents,
            value: params.value,
            currency: params.currency || 'MYR',
            num_items: params.num_items || params.content_ids.length,
            ...this.getUTMParams(), // Add UTM parameters
        };

        // Add order_id if provided (custom parameter)
        if (params.order_id) {
            (eventData as any).order_id = params.order_id;
        }

        // Track with event_id for deduplication
        window.fbq('track', 'Purchase', eventData, { eventID: eventId });


        // Return event data for server-side tracking
        return {
            eventId,
            fbp: this.getFBPCookie(),
            fbc: this.getFBCCookie(),
        };
    }

    /**
     * Set user data for advanced matching
     * Facebook automatically hashes this data
     */
    setUserData(userData: {
        email?: string;
        phone?: string;
        firstName?: string;
        lastName?: string;
        city?: string;
        state?: string;
        zip?: string;
        country?: string;
    }) {
        if (!this.initialized || !window.fbq) return;

        // Build user data object (Facebook will hash these automatically)
        const fbUserData: UserData = {};

        if (userData.email) fbUserData.em = userData.email.toLowerCase().trim();
        if (userData.phone) fbUserData.ph = userData.phone.replace(/\D/g, ''); // Remove non-digits
        if (userData.firstName) fbUserData.fn = userData.firstName.toLowerCase().trim();
        if (userData.lastName) fbUserData.ln = userData.lastName.toLowerCase().trim();
        if (userData.city) fbUserData.ct = userData.city.toLowerCase().trim();
        if (userData.state) fbUserData.st = userData.state.toLowerCase().trim();
        if (userData.zip) fbUserData.zp = userData.zip.replace(/\s/g, ''); // Remove spaces
        if (userData.country) fbUserData.country = userData.country.toLowerCase().trim();

        this.userData = fbUserData;

        // Update both pixels if they exist
        if (this.platformPixelId) {
            window.fbq('init', this.platformPixelId, fbUserData);
        }
        if (this.tenantPixelId) {
            window.fbq('init', this.tenantPixelId, fbUserData);
        }

    }

    /**
     * Track custom event
     */
    trackCustom(eventName: string, params?: Record<string, any>) {
        if (!this.initialized || !window.fbq) return;

        window.fbq('trackCustom', eventName, params);

    }

    /**
     * Generate unique event ID for Facebook Conversion API deduplication
     * Uses timestamp + random to ensure uniqueness
     */
    private generateEventId(): string {
        const timestamp = Date.now();
        const random = Math.random().toString(36).substring(2, 11);
        return `${timestamp}_${random}`;
    }

    /**
     * Get Facebook Browser Pixel cookie (_fbp)
     */
    private getFBPCookie(): string | null {
        const cookies = document.cookie.split('; ');
        const fbpCookie = cookies.find(cookie => cookie.startsWith('_fbp='));
        return fbpCookie ? fbpCookie.split('=')[1] : null;
    }

    /**
     * Get Facebook Click ID cookie (_fbc)
     */
    private getFBCCookie(): string | null {
        const cookies = document.cookie.split('; ');
        const fbcCookie = cookies.find(cookie => cookie.startsWith('_fbc='));
        return fbcCookie ? fbcCookie.split('=')[1] : null;
    }

    /**
     * Get UTM parameters from URL
     */
    private getUTMParams(): Record<string, string> {
        const params = new URLSearchParams(window.location.search);
        const utmParams: Record<string, string> = {};

        const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
        utmKeys.forEach(key => {
            const value = params.get(key);
            if (value) {
                utmParams[key] = value;
            }
        });

        return utmParams;
    }

    /**
     * Check if pixel is initialized
     */
    isInitialized(): boolean {
        return this.initialized;
    }

    /**
     * Get active pixel IDs
     */
    getPixelIds(): { platform?: string; tenant?: string } {
        return {
            platform: this.platformPixelId || undefined,
            tenant: this.tenantPixelId || undefined,
        };
    }
}

// Export singleton instance
export const facebookPixel = new FacebookPixel();
